import { Button, Empty, Form, Modal, Select, Tooltip, TreeSelect, Upload, UploadFile } from 'antd';
import React, { ReactNode } from 'react';
import classes from '../RelatedRooms.module.scss';
import { ExclamationCircleOutlined, FileAddOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { availableIconFormat, documnentIcons } from 'helpers/documentIcons';
import { useAppDispatch, useAppSelector } from 'store/hook';
import api from 'api';
import { useTranslation } from 'react-i18next';
import UploadingComponent from 'components/UploadingComponent/UploadingComponent';
import { RcFile } from 'antd/es/upload';
import { ConfirmationModal } from 'components/Modals';
import cn from 'classnames';
import { getTheme } from 'helpers/getTheme';
import RowLoader from 'components/Sceletons/RowLoader';
import { defaultGroups } from 'store/reducers/usersGroupCreator';
import { BaseOptionType } from 'antd/es/select';
import { setCurrentRelatedRoomTerms, setIsOpenAttachRoomModal } from 'store/slices/dataRoomSlice';
import { IDataRoom, IGroup } from 'interfaces';
import { checkIsTariffEnded, checkIsAwaitingPayment } from 'helpers/tariffPayment';

const isBillingActive = window.REACT_APP_IS_BILLING_ACTIVE !== 'REPLACE_REACT_APP_IS_BILLING_ACTIVE' ? window.REACT_APP_IS_BILLING_ACTIVE : process.env.REACT_APP_IS_BILLING_ACTIVE;

type ModalRelatedRoomType = {
  isOpen: boolean;
  onBeforeUpload: (file: UploadFile) => boolean;
  document: UploadFile[];
  setDocument: React.Dispatch<React.SetStateAction<UploadFile[]>>;
  getTermsOfAccess: () => Promise<void>;
};

const ModalRelatedRoom = ({ isOpen, onBeforeUpload, document, setDocument, getTermsOfAccess }: ModalRelatedRoomType) => {
  const [relatedRoomId, setRelatedRoomId] = React.useState<string>('');
  const [restrictedRoomsIds, setRestrictedRoomsIds] = React.useState<string[]>([]);
  const [cantCreateAccessPoint, setCantCreateAccessPoint] = React.useState<string[]>([]);
  const [relatedRoomsOrEndedRoomsIds, setRelatedOrEndedRoomsIds] = React.useState<string[]>([]);
  const [isLoadingRestrictedRooms, setIsLoadingRestrictedRooms] = React.useState<boolean>(false);
  const [uploadingRelatedRoom, setUploadingRelatedRoom] = React.useState<boolean>(false);
  const [confirmUploadingModal, setConfirmUploadingModal] = React.useState(false);
  const [form] = Form.useForm();
  const [selectedRoles, setSelectedRoles] = React.useState(['for_all_roles'])
  const [rightGroups, setRightGroups] = React.useState<IGroup[]>([]);
  const [loadingGroups, setLoadingGroups] = React.useState(false);  

  const { available_rooms } = useAppSelector(state => state.userData);
  const { currentRelatedRoomTerms, parentRoom } = useAppSelector(state => state.dataRoom);
  const { groups } = useAppSelector(state => state.dataUsers);
  const { messageApi } = useAppSelector((state) => state.windowState);  
  
  const { t } = useTranslation();  
  const dispatch = useAppDispatch();

  const checkChainOfRooms = async (relatedRoomId: string) => {
    setIsLoadingRestrictedRooms(true);
    try {
      const permissionsOfSelectedRoom = await api.getRoomsPermissions({room_id: relatedRoomId});
      if (!permissionsOfSelectedRoom.data?.can_create_access_point) {
        form.setFieldValue('room', null);
        messageApi?.warning(t('Settings.relatedRooms.noPermission'), 10);
        setCantCreateAccessPoint(prev => [relatedRoomId, ...prev]);
        return;
      }
      const chainOfRooms = await api.getChainOfRooms(parentRoom?.id!, relatedRoomId);      
      if (!chainOfRooms.data.is_available) {
        form.setFieldValue('room', null);
        messageApi?.warning(t('Settings.relatedRooms.disableToRelate'), 10);
        setRestrictedRoomsIds(prev => [relatedRoomId, ...prev]);
      }
    } catch (e) {
    } finally {
      setIsLoadingRestrictedRooms(false);
    }
  };

  React.useEffect(() => {
    form.setFieldsValue({roles: ['for_all_roles']});
    return () => {
      dispatch(setCurrentRelatedRoomTerms([]));
      setRightGroups([]);
      setRelatedRoomId('');
      setDocument([]);
    }
  },[]);

  const getRightGroups = async (parentRoom: IDataRoom) => {
    setLoadingGroups(true);
    try {
      const groupsOfParentRoom = await api.getGroups(parentRoom.id);
      const outGroups = groupsOfParentRoom.data.map((el) => ({
        ...el,
        id: el.group_id!,
        name: el.group_name!,
        key: el.group_id,
        first_name: el.group_name,
        emailOrRole: el.role?.name,
        type: 'group'
      }));      
      setRightGroups(outGroups);
    } catch (e) {
      messageApi?.error(t('Settings.relatedRooms.errorGetGroups', {room: parentRoom.name}), 10);
    } finally {
      setLoadingGroups(false);
    }
  };

  React.useEffect(() => {
    !rightGroups[0] && parentRoom && getRightGroups(parentRoom);
  },[parentRoom]);

  React.useEffect(() => {
    relatedRoomId && checkChainOfRooms(relatedRoomId);
  },[relatedRoomId]);

  React.useEffect(() => {
    const alreadyRelated = currentRelatedRoomTerms.map((term) => term.related_room?.id || '');
    let endedRoom: string[] = []
    if (isBillingActive === 'true') {
      endedRoom = available_rooms.map(room => 
        checkIsTariffEnded(room) || checkIsAwaitingPayment(room) ? room.id : ''  
      ).filter(el => !!el);
    }
    setRelatedOrEndedRoomsIds([...alreadyRelated, ...endedRoom]);
  },[currentRelatedRoomTerms]);

  const closeModal = () => dispatch(setIsOpenAttachRoomModal({isOpen: false, parentRoom: null}));

  const addNewRelatedRoom = async () => {
    setUploadingRelatedRoom(true);
    setConfirmUploadingModal(false);    
    try {
      const formData = new FormData();
      formData.append('document', document[0] as RcFile);      
      formData.append('related_room_id', relatedRoomId);
      formData.append('room_id', parentRoom?.id!);
      if (selectedRoles.includes('for_all_roles')) {
        formData.append('for_all_groups', 'true');
        formData.append('groups', '');
      } else {
        formData.append('for_all_groups', 'false');
        formData.append('groups', JSON.stringify(selectedRoles));
      }
      await api.addRelatedRoom(formData);
      messageApi?.success(t('Settings.relatedRooms.successAdded'));
      closeModal();
      getTermsOfAccess();
    } catch (e) {
      messageApi?.error(t('Settings.relatedRooms.errorAdding'));
    } finally {
      setUploadingRelatedRoom(false);
    }
  };

  const iconRender = (file: UploadFile) => {
    const format = file?.name?.split('.')?.reverse()[0];  
    const icon = availableIconFormat.includes(format) ? format : 'default';
    return documnentIcons[icon as keyof typeof documnentIcons];
  };
  
  const onRemove = (file: UploadFile<any>) => setDocument(document.filter((currentFile)=> file.uid !== currentFile.uid));
  
  const roomsOptions = React.useMemo(() => {
    const arrOptions: BaseOptionType[] = available_rooms
      .filter(room => room.id !== parentRoom?.id)
      .filter(room => room.invite_status !== 0) // отфильтровываем приглашение
      .map((room) => {
        const existLinkOnRoom = currentRelatedRoomTerms.find(term => term.related_room?.id === room.id);
        const isAlreadyCreatedLinkForAllGroups = existLinkOnRoom?.for_all_groups;
        const rolesAlreadyHaveLinks = existLinkOnRoom?.groups;
        const localizedRolesNames = rolesAlreadyHaveLinks?.map(group => defaultGroups.includes(group.name) ? t(`Users.table.${group.name}`) : group.name).join(', ');
        
        const overlappingRoles = [...selectedRoles].filter(item => rolesAlreadyHaveLinks?.map(group => group.id).includes(item));      
        const itMustNotCreateLinkForSelectedRoles = existLinkOnRoom?.groups ? (selectedRoles.includes('for_all_roles') || Boolean(overlappingRoles[0])) : false;
        
        const isRoomDisabled = restrictedRoomsIds.includes(room.id)
          || cantCreateAccessPoint.includes(room.id)
          || isAlreadyCreatedLinkForAllGroups
          || itMustNotCreateLinkForSelectedRoles;
        const needToShowTooltip = isRoomDisabled || relatedRoomsOrEndedRoomsIds.includes(room.id);

        let tooltipText: string = '';
        if (restrictedRoomsIds.includes(room.id)) tooltipText = t('Settings.relatedRooms.disableToRelate');
        else if (cantCreateAccessPoint.includes(room.id)) tooltipText = t('Settings.relatedRooms.noPermission');
        else if (isAlreadyCreatedLinkForAllGroups) tooltipText = t('Settings.relatedRooms.alreadyCreated');
        else if (rolesAlreadyHaveLinks) tooltipText = t('Settings.relatedRooms.alreadyCreatedFor', {groups: loadingGroups ? t('Settings.relatedRooms.loading') : localizedRolesNames});
        else if (relatedRoomsOrEndedRoomsIds.includes(room.id)) tooltipText = t('Settings.relatedRooms.tariffExpired');
        
        return {
          label: needToShowTooltip ? <Tooltip title={tooltipText}>
            <div style={{width: '100%', display: 'flex', justifyContent: 'space-between'}}>
              {room.name}
              <QuestionCircleOutlined style={{color: 'orange'}}/>
            </div>
          </Tooltip>
          : room.name,
          value: room.id,
          key: room.id,
          disabled: isRoomDisabled,
        }
      });
    return arrOptions;
  }, [available_rooms, groups, loadingGroups, selectedRoles, parentRoom?.id, currentRelatedRoomTerms, relatedRoomsOrEndedRoomsIds, cantCreateAccessPoint, restrictedRoomsIds]);

  const rolesOptions = React.useMemo(() => {
    const arrWithRoles: BaseOptionType[] = [
      {
        label: t('Settings.relatedRooms.for_all_roles'),
        value: 'for_all_roles',
        key: 'for_all_roles',
        children: loadingGroups
          ? [{
            label: <RowLoader height={22} width={180} padding={'0 0 0 0'}/>,
            value: 'loading',
            key: 'loading',
            disabled: true,
          }] : rightGroups.map(group => {
          const localizedGroupName = defaultGroups.includes(group.name) ? t(`Users.table.${group.name}`) : group.name;
          return {
            label: localizedGroupName,
            value: group.group_id,
            key: group.group_id,
          }
        })
      },
    ];
    return arrWithRoles;
  }, [rightGroups, loadingGroups]);

  const onChangeTreeSelect = (newValue: string[], label: ReactNode[], extra: any) => {
    const existLinkOnRoom = currentRelatedRoomTerms.find(term => term.related_room?.id === form.getFieldValue('room'));
    const rolesAlreadyHaveLinks = existLinkOnRoom?.groups;
    const localizedRolesNames = rolesAlreadyHaveLinks?.map(group => defaultGroups.includes(group.name) ? t(`Users.table.${group.name}`) : group.name).join(', ');
    
    if (newValue.includes('for_all_roles') && rolesAlreadyHaveLinks) {
      form.setFieldValue('room', undefined);
      setRelatedRoomId('');
      messageApi?.warning(t('Settings.relatedRooms.cantCreated', {groups: localizedRolesNames}), 10) // испроавить формулировку - не совсем понятная
    };

    if (rolesAlreadyHaveLinks && rolesAlreadyHaveLinks[0]) {
      const overlappingRoles = [...newValue].filter(item => rolesAlreadyHaveLinks.map(role => role.id)?.includes(item));    
      const localizedRolesNames = overlappingRoles
        .map((groupId => {
          const targetGroup = rolesAlreadyHaveLinks.find(group => group.id === groupId);
          return targetGroup && defaultGroups.includes(targetGroup?.name) ? t(`Users.table.${targetGroup?.name}`) : targetGroup?.name
        }))
        .join(', ');

      if (overlappingRoles[0]) {
        form.setFieldValue('room', undefined);
        setRelatedRoomId('');
        messageApi?.warning(t('Settings.relatedRooms.cantCreated', {groups: localizedRolesNames}), 10) // локализация + испроавить формулировку - не совсем понятная
      }
    }
    setSelectedRoles(newValue);
  };

  const tProps = {
    treeData: rolesOptions,
    value: selectedRoles,
    showSearch: false,
    onChange: onChangeTreeSelect,
    placeholder: t('Settings.relatedRooms.rolePlaceholder'),
    treeCheckable: true,
    showCheckedStrategy: TreeSelect.SHOW_PARENT ,// SHOW_CHILD, // SHOW_ALL,
    treeDefaultExpandAll: true,
    // treeCheckStrictly: true,
  };
  
  return (
    <Modal
      centered
      width={'60%'}
      onCancel={closeModal}
      open={isOpen}
      className={cn(classes.modalWrap, getTheme())}
      title={t('Settings.relatedRooms.modalTitle', {room: parentRoom?.name})}
      okText={t('main.saveButton')}
      okButtonProps={{disabled: isLoadingRestrictedRooms, loading: uploadingRelatedRoom}}
      onOk={form.submit}
    >
      <Form
        form={form}
        className={classes.form}
        onFinish={() => setConfirmUploadingModal(true)}
        layout='vertical'
      >
        <Form.Item
          name='roles'
          label={t('Settings.relatedRooms.roleSection')}
          rules={[{ required: true, message: t('Settings.relatedRooms.roleSectionWarning')}]}
        >
          <TreeSelect
            className={classes.select}
            loading={loadingGroups}
            {...tProps}
          />
        </Form.Item>

        <Form.Item
          name='room'
          label={t('Settings.relatedRooms.roomSection')}
          rules={[{ required: true, message: t('Settings.relatedRooms.roomSectionWarning')}]}
        >
          <Select
            className={classes.select}
            placeholder={t('Settings.relatedRooms.roomPlaceholder')}
            options={roomsOptions}
            showSearch
            filterOption={(input, option) =>
              String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            notFoundContent={<Empty
              description={t('Settings.relatedRooms.roomsEmpty')}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />}
            onChange={setRelatedRoomId}
            loading={isLoadingRestrictedRooms}
          />
        </Form.Item>
        <Form.Item
          name='file'
          label={t('Settings.relatedRooms.termSection')}
          rules={[
            {
              message: t('Settings.relatedRooms.termSectionWarning'),
              validator: (_, value) => {
                const fileExtension = value?.file?.name?.split('.')?.reverse()[0];                
                if (fileExtension && ['pdf', 'docx', 'doc'].includes(fileExtension)) {
                  return Promise.resolve();
                } else {
                  return Promise.reject();
                }
              }
            }
          ]}
        >
          <Upload 
            name='file'
            listType='picture'
            multiple={false}
            maxCount={1}
            fileList={document}
            onRemove={onRemove}
            className={classes.draggerModal}
            beforeUpload={onBeforeUpload}
            iconRender={iconRender}
            accept='.doc,.docx,.xml,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.pdf'      
          >
            {
              !document[0] && 
                <Button icon={<FileAddOutlined className={classes.fileIcon}/>}>{t('Settings.relatedRooms.chooseTerm')}</Button>
            }
          </Upload>
        </Form.Item>
      </Form>
      {(uploadingRelatedRoom || isLoadingRestrictedRooms) && <UploadingComponent />}

      {confirmUploadingModal && (
        <React.Suspense fallback={<div />}>
          <ConfirmationModal
            isOpen={confirmUploadingModal}
            onCancel={() => setConfirmUploadingModal(false)}
            isLoading={uploadingRelatedRoom}
            centered={true}
            submit={addNewRelatedRoom}
            okText={t('Settings.terms.confirmModal.okButton')}
            text={<div className={classes.textModal}>
              <p>1. {t('Settings.terms.confirmModal.textReminder')}</p>
              <p>2. {t('Settings.terms.confirmModal.text')}</p>
            </div>}
            title={<div className={classes.titleRow}>
              <ExclamationCircleOutlined className={classes.exclamationIcon}/>{t('Settings.terms.confirmModal.titleImportant')}
            </div>}
          />
        </React.Suspense>
      )}
    </Modal>
  );
}

export default ModalRelatedRoom;
